import { useUserDetailContext } from "Components/UserDetailContext.js";
import { formatChatTimestamp } from "Components/Utilities/DateUtils.js";
import { MessageCircle, Search, Users } from "lucide-react";
import { TbMessage2Plus } from "react-icons/tb"
import { memo, useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getSocket } from "services/socketService.js";
import {
  getConversationsList,
  getDirectMessages,
  getGroupMessages,
  setCurrentConversation,
  setOnlineUsers,
  setUnreadCount,
} from "../../../../../../Redux/Reducers/cceReducer.js";
import {
  getAllConversations,
  getGroupMembers,
} from "../../../../../../Redux/Reducers/ContactReducer.js";
import "../.././global.css";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar.jsx";
import SendNewMessage from "./SendNewMessage.jsx";
import ChatInfoSheet from "./ChatInfoSheet.jsx";


function ChatSidebar({setIsSidebarOpen }) {
  const socket = getSocket();
  const [activeFilters, setActiveFilters] = useState({
    groups: false,
    members: false,
  });
  const [searchValue, setSearchValue] = useState(""); // Fixed state name
  const [openDm, setOpenDm] = useState(false);
  const onSearchChange = (value) => {
    setSearchValue(value); // Actually update the search value
  };

  const toggleFilter = (filter) => {
    setActiveFilters((prev) => ({
      ...prev,
      [filter]: !prev[filter],
    }));
  };

  // Show all when both are false or both are true
  const showAll =
    (!activeFilters.groups && !activeFilters.members) ||
    (activeFilters.groups && activeFilters.members);
  const showGroups = showAll || activeFilters.groups;
  const showPeoples = showAll || activeFilters.members;

  const userConverations = useSelector(
    (state) => state?.ContactArray?.allConversations
  );

  const { userDetails } = useUserDetailContext();
  const activeConversation = useSelector(
    (state) => state?.ContactArray?.activeConversation
  );
  const unreadCounts = useSelector((state) => state?.cce?.unreadCounts);
  const members = activeConversation?.payload?.members || [];
  const referenceId = userDetails?.reference_id;
  const dispatch = useDispatch();
  const onlineUsers = useSelector((state) => state?.cce?.onlineUsers);

  // console.log("activeConversation-2", activeConversation)

  // Create base conversations list
  const baseConversationsList = useMemo(() => {
    const sortedConversations = userConverations?.conversations
      ?.filter((item) => item?.last_message !== null)
      ?.sort((a, b) => {
        const unreadA = unreadCounts?.find(
          (u) => u.conversationId === a.conversation_id
        );
        const unreadB = unreadCounts?.find(
          (u) => u.conversationId === b.conversation_id
        );

        const timeA = unreadA?.latestMessageTime || a?.last_message?.created_at;
        const timeB = unreadB?.latestMessageTime || b?.last_message?.created_at;

        return new Date(timeB).getTime() - new Date(timeA).getTime();
      });

    return sortedConversations?.map((item) => {
      // console.log("iam Item", item)
      const title =
        item?.source_type == "direct"
          ? `${item?.participant?.first_name} ${item?.participant?.last_name}`
          : item?.participant?.group_name;

      return {
        conversationId: item?.conversation_id,
        sourceId: item?.sourceId,
        sourceType: item?.source_type,
        userId: item?.participant?.reference_id,
        title: title,
        avatar: item?.participant?.profile_url || item?.participant?.group_image,
        lastMessage: item?.last_message?.content,
        timestamp: item?.last_message?.created_at,
        isTyping: false,
        isOnline: onlineUsers?.includes(item?.sourceId) ? true : false,
        unreadCount: unreadCounts?.find(
          (i) => i?.conversationId == item?.conversation_id
        )?.unreadCount || "",
      };
    }) || [];
  }, [userConverations, unreadCounts, onlineUsers]);

  // Apply filters and search to create filtered list
  const filteredConversationsList = useMemo(() => {
    let filtered = baseConversationsList;

    // Apply type filters
    if (!showAll) {
      filtered = filtered.filter((conversation) => {
        if (activeFilters.groups && conversation.sourceType === "group") {
          return true;
        }
        if (activeFilters.members && conversation.sourceType === "direct") {
          return true;
        }
        return false;
      });
    }

    // Apply search filter
    if (searchValue.trim()) {
      const searchTerm = searchValue.toLowerCase().trim();
      filtered = filtered.filter((conversation) =>
        conversation.title.toLowerCase().includes(searchTerm)
      );
    }

    return filtered;
  }, [baseConversationsList, showAll, activeFilters, searchValue]);

  // console.log("filteredConversationsList", filteredConversationsList)

  // Counts for filter chips
  const groupCount = baseConversationsList.filter(c => c?.sourceType === "group").length;
  const memberCount = baseConversationsList.filter(c => c?.sourceType === "direct").length;

  // Check if the user is a member of this group and ADMIN
  const currentMember = members.find(
    (member) => member.user_ref_id === referenceId
  );
  const isAdmin = currentMember?.role === "ADMIN";

  useEffect(() => {
    dispatch(getAllConversations());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getConversationsList());
    if (!socket) return;
    const handleOnline = (data) => {
      console.log("Online Users:", data);
      dispatch(setOnlineUsers(data));
    };

    const handleUnread = (data) => {
      console.log("Unread Counts my:", data);
      dispatch(setUnreadCount(data));
    };

    socket?.emit("envoke-unread-counts", userDetails?.reference_id);
    socket?.emit("user-online", userDetails?.reference_id);
    socket.on("online-users", handleOnline);
    socket.on("unread-counts", handleUnread);

    return () => {
      socket.off("online-users", handleOnline);
      socket.off("unread-counts", handleUnread);
    };
  }, [socket]);

  const handleChat = async (item) => {
    // console.log("IAMfromItem", item);
    if (typeof item == "object") {
      dispatch(setCurrentConversation(item));
      if (item?.sourceType == "direct") {
        dispatch(getDirectMessages({ id: item?.conversationId }));
      } else {
        dispatch(getGroupMessages({ id: item?.conversationId }));
      }

      if (item?.sourceType == "group") {
        dispatch(getGroupMembers(item?.sourceId));
      }
    }
  };

  const handleMessageOpen=()=>{
    setIsSidebarOpen(false)
    setOpenDm(true)

  }

  const renderConversationRow = (item) => {
    return (
      <li key={item.conversationId}>
        <div
          role="button"
          tabIndex={0}
          onClick={() => handleChat(item)}
          className={`flex w-full items-center gap-3 rounded-2xl px-3 py-2 text-left transition ${
            item?.isOnline ? "" : "hover:bg-[#202c33]/15"
          }`}
        >
          <div className="relative">
            <Avatar className="h-10 w-10">
              <AvatarImage
                src={item?.avatar|| 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcS6LXNJFTmLzCoExghcATlCWG85kI8dsnhJng&s'}
                alt={item?.title}
                referrerPolicy="no-referrer"
              />
              {!item?.sourceType == "group" ? <AvatarFallback>{item?.title}</AvatarFallback> : ""}
              <AvatarFallback>
                {item?.sourceType == "group" ? (
                  <Users className="h-5 w-5" />
                ) : null}
              </AvatarFallback>
            </Avatar>
            {item?.sourceType == "direct" && item?.isOnline && (
              <span
                className={`absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-[#0b141a] bg-green-500`}
              />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              {/* Left side - Name and message */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <span
                    className={`text-sm font-medium text-[#e9edef] truncate ${
                      item?.unreadCount ? "font-semibold text-black" : ""
                    }`}
                  >
                    {item.title}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span
                    className={`text-xs truncate ${
                      item?.unreadCount
                        ? "font-semibold text-black"
                        : "text-[#8696a0]"
                    }`}
                  >
                    {item.isTyping
                      ? "typing…"
                      : item.lastMessage || "Tap to catch up"}
                  </span>
                </div>
              </div>

              {/* Right side - Timestamp and unread count */}
              <div className="flex flex-col items-end gap-1 flex-shrink-0 ml-2">
                {item?.unreadCount && (
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#25d366] text-xs font-medium text-white">
                    {item?.unreadCount > 99 ? "99+" : item?.unreadCount}
                  </span>
                )}
                {item?.timestamp && (
                  <span className="text-[11px] text-[#8696a0] whitespace-nowrap">
                    {formatChatTimestamp(item?.timestamp)}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </li>
    );
  };

  return (
    <aside className="flex h-full w-full flex-col border-r border-[#1f2c34] bg-[#0b141a] text-[#e9edef]">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#1f2c34]">
        <Avatar className="h-10 w-10">
          <AvatarImage
            src={userDetails?.proFile || 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcS6LXNJFTmLzCoExghcATlCWG85kI8dsnhJng&s'}
            alt="You"
            referrerPolicy="no-referrer"
          />
          <AvatarFallback>You</AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <div className="text-sm font-semibold">Chats</div>
          <div className="text-[11px] text-[#8696a0]">
            Stay connected securely
          </div>
        </div>
        <button
          type="button"
          onClick={handleMessageOpen}
          className="grid h-9 w-9 place-items-center rounded-full bg-[#202c33] text-[#d1d7db] hover:bg-[#1f2c34]"
          aria-label="Start new chat"
        >
          {/* <MessageCircle className="h-4 w-4" />
           */}
           <TbMessage2Plus />
        </button>
        {isAdmin && (
          <button
            type="button"
            onClick={() => {}}
            className="grid h-9 w-9 place-items-center rounded-full bg-[#202c33] text-[#d1d7db] hover:bg-[#1f2c34]"
            aria-label="Create group"
          >
            <Users className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="border-b border-[#1f2c34] px-4 py-3">
        <label className="relative flex items-center">
          <Search className="pointer-events-none absolute left-3 h-4 w-4 text-[#8696a0]" />
          <input
            value={searchValue}
            onChange={(event) => onSearchChange(event.target.value)} // Fixed: actually call the function
            className="h-9 w-full rounded-xl bg-[#202c33] pl-10 pr-3 text-sm text-[#e9edef] placeholder:text-[#8696a0] focus:outline-none focus:ring-2 focus:ring-[#25d366]"
            placeholder="Search or start new chat"
            aria-label="Search chats"
          />
        </label>

        {/* Filter Chips */}
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={() => toggleFilter("groups")}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
              activeFilters?.groups
                ? "bg-[#25d366] text-[#0b141a]"
                : "bg-[#202c33] text-[#8696a0] hover:bg-[#1f2c34]"
            }`}
          >
            Groups ({groupCount})
          </button>
          <button
            type="button"
            onClick={() => toggleFilter("members")}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
              activeFilters?.members
                ? "bg-[#25d366] text-[#0b141a]"
                : "bg-[#202c33] text-[#8696a0] hover:bg-[#1f2c34]"
            }`}
          >
            Members ({memberCount})
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Filtered Conversations List */}
        {filteredConversationsList?.length > 0 ? (
          <ul className="space-y-1 px-2 py-2">
            {filteredConversationsList?.map((item) => renderConversationRow(item))}
          </ul>
        ) : (
          <div className="flex flex-col items-center justify-center h-40 text-[#8696a0] p-4 text-center">
            <Users className="h-8 w-8 mb-2" />
            <p className="text-sm">
              {searchValue.trim() ? "No conversations match your search" : "No conversations found"}
            </p>
            <p className="text-xs mt-1">
              {searchValue.trim() ? "Try adjusting your search terms" : ""}
            </p>
          </div>
        )}
      </div>

      {openDm && <SendNewMessage openDm={openDm} setOpenDm={setOpenDm} />}
     
    </aside>
  );
}

export default memo(ChatSidebar);